import React from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, ReferenceDot,
} from "recharts";
import { useI18n } from "@/lib/i18n";

function CustomTooltip({ active, payload, label, layers, metrics }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bg-[#0A0A0A] border border-[var(--neon-cyan)] p-3 font-mono text-[11px] min-w-48">
      <div className="text-[10px] uppercase tracking-[0.18em] neon-cyan mb-2">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center justify-between gap-3 text-white">
          <span className="flex items-center gap-2">
            <span className="w-2 h-2" style={{ background: p.color }}/>
            {p.name}
          </span>
          <span style={{ color: p.color }}>{typeof p.value === "number" ? p.value.toFixed(2) : p.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function GlaserChart({ metrics, layers, dark = true }) {
  const { t } = useI18n();
  if (!metrics) return null;

  const labels = [t("glaser.label.airIn", "Indoor air"), t("glaser.label.surfaceIn", "Indoor surface")];
  for (let i = 0; i < layers.length - 1; i++) labels.push(`L${i + 1}/L${i + 2}`);
  labels.push(t("glaser.label.surfaceOut", "Outdoor surface"));

  const tempKey = t("glaser.legend.temperature", "Temperature °C");
  const dewKey  = t("glaser.legend.dew", "Dew Point");
  const vaporKey = t("glaser.legend.vapor", "Vapor Pressure (Pa)");
  const satKey   = t("glaser.legend.saturation", "Saturation Pressure (Pa)");

  const tempData = metrics.temperatures.map((tt, i) => ({
    point: labels[i] || `P${i}`,
    [tempKey]: Number(tt.toFixed(2)),
    [dewKey]:  Number(metrics.dew_point.toFixed(2)),
  }));

  const vp = metrics.vapor_pressure || [];
  const sp = metrics.saturation_pressure || [];
  const pData = vp.map((v, i) => ({
    point: labels[i] || `P${i}`,
    [vaporKey]: Math.round(v),
    [satKey]:   Math.round(sp[i] ?? 0),
  }));

  const condIdx = metrics.condensation_indices || [];

  const axisColor = "#FFFFFF";
  const gridColor = "#374151";
  const surfaceBg = "bg-[#000000]";
  const borderC = dark ? "border-[#1F1F1F]" : "border-[#D4D4D4]";

  return (
    <div data-testid="glaser-chart">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-base font-semibold tracking-tight">{t("glaser.title2", "Glaser Analysis")}</h3>
        <span className="tech-label">{t("glaser.interactive", "INTERACTIVE · HOVER")}</span>
      </div>

      <div className={`border ${borderC} ${surfaceBg} p-2`} style={{ height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={tempData} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
            <CartesianGrid stroke={gridColor} strokeDasharray="0"/>
            <XAxis dataKey="point" tick={{ fontSize: 9, fontFamily: "IBM Plex Mono", fill: axisColor }}/>
            <YAxis tick={{ fontSize: 9, fontFamily: "IBM Plex Mono", fill: axisColor }}/>
            <Tooltip content={(props) => <CustomTooltip {...props} layers={layers} metrics={metrics}/>}/>
            <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono", fontSize: 9, textTransform: "uppercase" }}/>
            <Line type="linear" dataKey={tempKey} stroke="#FF3B30" strokeWidth={2.2}
              dot={{ r: 3, fill: "#FF3B30", stroke: "#0A0A0A", strokeWidth: 1 }}
              activeDot={{ r: 6, fill: "#FF3B30", stroke: "#FFFFFF", strokeWidth: 2 }}
              isAnimationActive={false}/>
            <Line type="linear" dataKey={dewKey} stroke="#00E5FF" strokeDasharray="3 3" strokeWidth={1.5}
              dot={false} isAnimationActive={false}/>
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className={`border ${borderC} ${surfaceBg} p-2 mt-2`} style={{ height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={pData} margin={{ top: 8, right: 12, bottom: 0, left: -4 }}>
            <CartesianGrid stroke={gridColor} strokeDasharray="0"/>
            <XAxis dataKey="point" tick={{ fontSize: 9, fontFamily: "IBM Plex Mono", fill: axisColor }}/>
            <YAxis tick={{ fontSize: 9, fontFamily: "IBM Plex Mono", fill: axisColor }}/>
            <Tooltip content={(props) => <CustomTooltip {...props} layers={layers} metrics={metrics}/>}/>
            <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono", fontSize: 9, textTransform: "uppercase" }}/>
            <Line type="linear" dataKey={vaporKey} stroke="#00E5FF" strokeWidth={2.2}
              dot={{ r: 3, fill: "#00E5FF", stroke: "#0A0A0A", strokeWidth: 1 }}
              activeDot={{ r: 6, fill: "#00E5FF", stroke: "#FFFFFF", strokeWidth: 2 }}
              isAnimationActive={false}/>
            <Line type="linear" dataKey={satKey} stroke="#39FF14" strokeDasharray="3 3" strokeWidth={1.6}
              dot={false} isAnimationActive={false}/>
            {condIdx.map((idx, k) => (
              <ReferenceDot key={k} x={labels[idx]} y={vp[idx]} r={6} fill="#FF3B30" stroke="#FFFFFF" strokeWidth={1}/>
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-2 grid grid-cols-2 gap-2 font-mono text-[10px]">
        <div className="flex items-center gap-1">
          <span className="w-2 h-2 bg-[#FF3B30] inline-block"/>
          {t("glaser.condensationPoints", "Condensation points")}: {condIdx.length}
        </div>
        <div className="text-right text-[#8B8B95]">
          Σs<sub>d</sub> = {metrics.total_sd?.toFixed(2)} m
        </div>
      </div>
    </div>
  );
}
